import { useEffect, useRef } from 'react';

/**
 * A hook to use requestAnimationFrame inside the react lifecycle.
 * - Initializes animation calls in a useEffect.
 * - On each frame, calls the provided `animationCallback` with the time delta between the current call and previous call.
 *
 * **NOTE:** when dealing with react states, typically update states with callbacks to assure the correct previous state is used.
 *
 * _Source:_ https://css-tricks.com/using-requestanimationframe-with-react-hooks/
 * @param animationCallback the function to run on each animation frame.
 * @param isPaused a boolean to denote whether the animation should be played or paused.
 * `false` will trigger another call to requestAnimationFrame.
 * `true` will call cancelAnimationFrame.
 * @param deps a dependency array for the useEffect that triggers the beginning of the animation frame calls.
 */
const useAnimationFrame = (animationCallback: (time: DOMHighResTimeStamp) => void, isPaused = false, deps: React.DependencyList = []) => {
  // Use useRef for mutable variables that we want to persist without triggering a re-render on their change
  const animation = useRef<number>();
  const previousTimeRef = useRef<number>();

  const animationFunc = (time: number) => {
    if (previousTimeRef.current !== undefined) {
      animationCallback(time);
    }
    previousTimeRef.current = time;
    animation.current = requestAnimationFrame(animationFunc);
  };

  useEffect(() => {
    if (!isPaused) {
      animation.current = requestAnimationFrame(animationFunc);
    } else {
      cancelAnimationFrame(animation.current);
    }

    return () => cancelAnimationFrame(animation.current);
  }, [isPaused, ...deps]);
};

export default useAnimationFrame;
